// Billing API — subscription overview, promocode, the properties covered,
// payments.
//
// The price list of 5 Oct 2026: two products, each a monthly price for every
// covered property and a monthly price for each of its rooms, and the product
// follows the property — a subscription chooses no plan. The types below are
// that answer. The page and the API deploy separately, so every field the
// list added is optional: for the minutes the page meets the build before it,
// the quote arrives without them and the page draws that build's own total
// (see `PriceBreakdownCard`). The fields of the list it replaced — a fixed
// fee, its price per room, bands of units, a chosen plan, OPTIVO Portfolio's
// wait for an individual quote — are not typed here: the API still sends them
// for a page loaded before this one, and this page reads none of them.

import { apiFetch } from "./apiClient";

export type BillingStatus = "trialing" | "active" | "unpaid";
export type PaymentMethod = "wire" | "gopay";
/**
 * `refund_due` is money received for a period another payment had already
 * paid: nothing was applied and it is to be given back. `refunded` says it
 * was. An API later than this page may send a status it does not list; a
 * screen says such a one generically and never looks its words up blind.
 */
export type PaymentStatus = "pending" | "paid" | "canceled" | "failed" | "refund_due" | "refunded";

/**
 * The automatic charge for the renewal now due that took nothing. A renewal
 * is charged once, so while this stands the card is not tried again: the
 * period is paid another way, and the mandate is charged for the one after.
 */
export interface RecurringFailure {
    /** When the charge was found to have failed. */
    at: string;
    /**
     * `declined`, `timed_out` or `refused` — or a kind a later API names.
     * The page and the API deploy separately, so it is read as text: the
     * page has a sentence for the kinds it knows and prints `reason` for
     * the rest.
     */
    kind: string;
    /** What the server recorded with it, in English. */
    reason: string | null;
}

export interface RecurringMandate {
    enabled: boolean;
    gopayParentId: string | null;
    /** Last day the mandate may be charged. */
    until: string | null;
    startedAt: string | null;
    lastChargeAt: string | null;
    /**
     * The failed charge of the renewal now due; null when none failed.
     * Absent from an API that predates it (5 Oct 2026), which says all it
     * has to say in `lastError`.
     */
    failure?: RecurringFailure | null;
    /**
     * Why the last pass charged nothing, or why the outcome of its charge is
     * not known. Absent from an API that predates `failure`.
     */
    note?: string | null;
    /**
     * The one line an older API sends, and a newer one still does for the
     * page loaded before it: the failed charge's reason, else the note.
     */
    lastError: string | null;
}

export interface BillingSubscription {
    id: number;
    tenantId: number;
    currency: string;
    trialStart: string;
    trialEnd: string;
    promoCode: string | null;
    promoAppliedAt: string | null;
    /**
     * Support's monthly price for the account, in CZK; null = the price list.
     * It replaces the list price of every covered property.
     */
    individualMonthlyPrice?: number | null;
    /** Support's monthly market-data surcharge, billed on top, in CZK; null = none. */
    marketDataMonthlyPrice?: number | null;
    /** Whether a market-data surcharge is billed — market data itself comes with both products. */
    marketDataEnabled: boolean;
    billingInterval: BillingInterval;
    /** Connections the subscription pays for; `null` = all of them, standing. */
    billedConnectionIds: number[] | null;
    paidUntil: string | null;
    status: BillingStatus;
    recurring: RecurringMandate;
}

export type BillingInterval = "month" | "year";

/**
 * The products of the price list, by key — mirrors `BillingPlan` in the
 * pricing service: OPTIVO Hotels (`pro`, the key it had while it was a plan)
 * and OPTIVO Rentals. A key names the product a property is priced on, by
 * what the property is; nobody chooses one.
 */
export type BillingPlan = "pro" | "rentals";

export type BillingPlanFamily = "hotels" | "rentals";

/** A product as the price list states it, priced in the subscription's currency. */
export interface BillingProductOffer {
    key: BillingPlan;
    family: BillingPlanFamily;
    /** One covered property for one month, before its rooms. */
    propertyMonthlyPrice: number;
    /** One of its rooms for one month. */
    roomMonthlyPrice: number;
    /** Prices go to the PMS up to this many times a day. */
    uploadsPerDay: number;
    /** How many days ahead the prices sent reach. */
    horizonDays: number;
    /** Revenue studies included in a quarter. */
    analysesPerQuarter: number;
    /** Whether market data comes with the product at no charge. */
    marketDataIncluded: boolean;
}

/**
 * One billed property on a quote: the list price of the product it is, and
 * of its rooms.
 */
export interface BillingPropertyPrice {
    connectionId: number;
    name: string | null;
    /** The rooms Optivo prices in it — units, where it is a rental. Each is charged for. */
    roomCount: number;
    plan: BillingPlan;
    family: BillingPlanFamily;
    /** The product's price of a property for one month, before its rooms. */
    propertyMonthlyPrice: number;
    /** One room for one month. */
    roomMonthlyPrice: number;
    /** Its rooms for one month. */
    roomsMonthlyPrice: number;
    /** One month: the property and its rooms. */
    monthlyPrice: number;
    /** The property for the whole term, before its rooms. */
    propertyTermPrice: number;
    /** Its rooms for the whole term. */
    roomsTermPrice: number;
    /** The whole term. */
    termPrice: number;
}

export interface MonthlyPricing {
    currency: string;
    interval: BillingInterval;
    /** Months this quote covers — 1 or 12. */
    months: number;
    /** Rooms across every covered property: at list price each is charged for. */
    roomCount: number;
    /** Covered properties with at least one room priced. */
    propertyCount?: number;
    /**
     * One entry per billed property at its list price; empty under an
     * individual price, and where no covered property has a room priced.
     * Absent from an API that predates the list of 5 Oct 2026 — the page
     * then draws that build's total as one row and states no list price.
     */
    properties?: BillingPropertyPrice[];
    /** Every property and its rooms for the whole term; 0 under an individual price. */
    propertiesTotal?: number;
    /** Support's price for the whole term; null when the list price applies. */
    individualPrice?: number | null;
    /** A market-data charge is on the quote: the surcharge support agreed. */
    marketDataEnabled: boolean;
    /** Market data comes with the list price, and no surcharge is billed. */
    marketDataIncluded?: boolean;
    /** What is charged for market data over the term; 0 when nothing is. */
    marketDataPrice: number;
    /**
     * What is charged for the term before VAT: everything above, added up.
     * Nothing is taken off it — Optivo gives no discount since 5 Oct 2026, and
     * a year is twelve months at the monthly price. (The API before that still
     * takes a month off a year; its `total` is already net of it, which is
     * what the page reads.)
     */
    total: number;
    totalCzk: number;
    /** `total / months` — one month of the whole bill, on either term. */
    monthlyEquivalent: number;
}

export interface BillingPayment {
    id: number;
    method: PaymentMethod;
    status: PaymentStatus;
    amount: number;
    currency: string;
    roomCount: number;
    marketDataIncluded: boolean;
    periodStart: string;
    periodEnd: string;
    variableSymbol: string | null;
    gopayPaymentId: string | null;
    /** Connections this payment covered, frozen at creation; `null` = all. */
    billedConnectionIds: number[] | null;
    /**
     * When the payer agreed to the terms. Set on a wire, where Optivo takes
     * the agreement itself; null on a card, where the gateway does.
     */
    termsAcceptedAt?: string | null;
    paidAt: string | null;
    /** When it was recorded that a payment to be refunded was given back. */
    refundedAt?: string | null;
    createdAt: string;
}

export interface BillingBankAccount {
    iban: string;
    bic: string;
    accountNumber: string;
    recipientName: string;
}

export interface BilledConnectionRooms {
    connectionId: number;
    name: string | null;
    /**
     * Physical rooms counted; always 0 for a property that is not billed, and
     * for one whose PMS could not be read. A covered property with none is
     * not billed; at list price each room of one that is billed is charged for.
     */
    roomCount: number;
    /** Whether the subscription pays for this property. */
    included: boolean;
    /**
     * What its operator stated it to be, on the setup wizard's Property
     * Profile step; null where nobody has, absent from an older API. It
     * decides the product the property is priced on, whatever its size.
     */
    propertyType?: string | null;
}

export interface BillingRooms {
    roomCount: number;
    /**
     * A covered property's PMS could not be read: it counted no room, so it
     * is missing from the quote.
     */
    incomplete: boolean;
    /** Every configured connection, billed or not — the choice on offer. */
    perConnection: BilledConnectionRooms[];
    /** The covered connections that actually exist. */
    billedConnectionIds: number[];
}

export type VatMode = "none" | "standard" | "reverse_charge" | "outside_scope";

export interface BillingTax {
    mode: VatMode;
    ratePercent: number;
    vatTotal: number;
    /** The amount actually charged — VAT included when it applies. */
    grandTotal: number;
}

export interface BillingCustomer {
    name: string | null;
    street: string | null;
    city: string | null;
    zip: string | null;
    country: string | null;
    ico: string | null;
    dic: string | null;
}

export interface BillingInvoice {
    id: number;
    invoiceNumber: string;
    issueDate: string;
    taxableSupplyDate: string;
    currency: string;
    vatMode: VatMode;
    vatRatePercent: number;
    baseTotal: number;
    vatTotal: number;
    grandTotal: number;
    grandTotalCzk: number;
    paymentMethod: string | null;
    variableSymbol: string | null;
}

export interface BillingOverview {
    subscription: BillingSubscription;
    pricing: MonthlyPricing;
    tax: BillingTax;
    customer: BillingCustomer;
    rooms: BillingRooms;
    /**
     * The price list, from the table that prices the quote, so the page
     * states it without typing a figure. Absent from an API that predates the
     * list of 5 Oct 2026, when the page states none.
     */
    products?: BillingProductOffer[];
    /**
     * The property types OPTIVO Rentals is for — a guesthouse, an apartment,
     * a vacation rental — so the page can say which properties it prices
     * without a rule of its own. Absent from an older API.
     */
    rentalsPropertyTypes?: string[];
    payments: BillingPayment[];
    invoices: BillingInvoice[];
    bank: BillingBankAccount;
    gopayAvailable: boolean;
}

export interface WirePaymentResult {
    payment: BillingPayment;
    /** SPAYD descriptor string — the payload of the payment QR code. */
    spayd: string;
    bank: BillingBankAccount;
}

export interface GopayPaymentResult {
    payment: BillingPayment;
    gatewayUrl: string;
}

/** Error carrying the backend's machine-readable code (e.g. PROMO_ALREADY_APPLIED). */
export class BillingApiError extends Error {
    constructor(
        message: string,
        public readonly code?: string,
    ) {
        super(message);
        this.name = "BillingApiError";
    }
}

async function unwrapError(res: Response, fallback: string): Promise<never> {
    const data = await res.json().catch(() => ({}));
    throw new BillingApiError(data.error || `${fallback}: ${res.status}`, data.code);
}

export async function fetchBilling(): Promise<BillingOverview> {
    const res = await apiFetch("/api/billing");
    if (!res.ok) await unwrapError(res, "fetchBilling failed");
    const data = await res.json();
    if (!data.success) throw new BillingApiError(data.error || "Failed to fetch billing");
    return data.billing;
}

export async function applyBillingPromoCode(code: string): Promise<BillingSubscription> {
    const res = await apiFetch("/api/billing/promocode", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
    });
    if (!res.ok) await unwrapError(res, "applyBillingPromoCode failed");
    const data = await res.json();
    if (!data.success) throw new BillingApiError(data.error || "Failed to apply promo code");
    return data.subscription;
}

// Billing is CZK-only for now, so the payload carries no currency — the API
// still refuses anything but CZK if an older client sends one. It carries no
// plan either: the product follows the property since 5 Oct 2026, and the API
// only still accepts one from a page loaded before that.
export async function updateBillingSubscription(updates: {
    billingInterval?: BillingInterval;
    /** Properties to pay for; `null` covers all of them, standing. */
    billedConnectionIds?: number[] | null;
}): Promise<BillingSubscription> {
    const res = await apiFetch("/api/billing/subscription", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
    });
    if (!res.ok) await unwrapError(res, "updateBillingSubscription failed");
    const data = await res.json();
    if (!data.success) throw new BillingApiError(data.error || "Failed to update subscription");
    return data.subscription;
}

export interface BillingStatusInfo {
    status: BillingStatus;
    trialEnd: string;
    paidUntil: string | null;
}

/**
 * Lightweight subscription status for the sidebar indicator — one row read,
 * none of the PMS room counting the full overview does.
 */
export async function fetchBillingStatus(): Promise<BillingStatusInfo> {
    const res = await apiFetch("/api/billing/status");
    if (!res.ok) await unwrapError(res, "fetchBillingStatus failed");
    const data = await res.json();
    if (!data.success) throw new BillingApiError(data.error || "Failed to fetch billing status");
    return data.status;
}

export async function updateBillingCustomer(
    customer: Partial<BillingCustomer>,
): Promise<BillingSubscription> {
    const res = await apiFetch("/api/billing/customer", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(customer),
    });
    if (!res.ok) await unwrapError(res, "updateBillingCustomer failed");
    const data = await res.json();
    if (!data.success) throw new BillingApiError(data.error || "Failed to update billing details");
    return data.subscription;
}

/** URL of the printable invoice document (opens in a new tab; print → PDF). */
export function invoiceHtmlUrl(invoiceId: number, lang: string): string {
    return `/api/billing/invoices/${encodeURIComponent(invoiceId)}/html?lang=${encodeURIComponent(lang)}`;
}

/** URL of the accountant CSV export, optionally scoped to one year. */
export function invoicesCsvUrl(year?: number): string {
    return year !== undefined
        ? `/api/billing/invoices/export.csv?year=${encodeURIComponent(year)}`
        : "/api/billing/invoices/export.csv";
}

/**
 * @param termsAccepted the payer's explicit agreement. A wire has no gateway
 * to take it, so the server refuses the payment without it — passing a
 * hard-coded `true` here would defeat the point of asking.
 */
export async function createWirePayment(
    termsAccepted: boolean,
): Promise<WirePaymentResult> {
    const res = await apiFetch("/api/billing/payments", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ method: "wire", termsAccepted }),
    });
    if (!res.ok) await unwrapError(res, "createWirePayment failed");
    const data = await res.json();
    if (!data.success) throw new BillingApiError(data.error || "Failed to create payment");
    return { payment: data.payment, spayd: data.spayd, bank: data.bank };
}

/**
 * @param recurring when true, the card also authorises automatic renewal at
 * the end of each paid period — the customer must have been shown the
 * mandate's terms first.
 */
export async function createGopayPayment(
    recurring: boolean = false,
): Promise<GopayPaymentResult> {
    const res = await apiFetch("/api/billing/payments", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ method: "gopay", recurring }),
    });
    if (!res.ok) await unwrapError(res, "createGopayPayment failed");
    const data = await res.json();
    if (!data.success) throw new BillingApiError(data.error || "Failed to create payment");
    return { payment: data.payment, gatewayUrl: data.gatewayUrl };
}

/** Revoke the card-on-file mandate, at the gateway and here. */
export async function cancelRecurringPayments(): Promise<BillingSubscription> {
    const res = await apiFetch("/api/billing/recurring", { method: "DELETE" });
    if (!res.ok) await unwrapError(res, "cancelRecurringPayments failed");
    const data = await res.json();
    if (!data.success) throw new BillingApiError(data.error || "Failed to cancel renewal");
    return data.subscription;
}

export async function refreshBillingPayment(paymentId: number): Promise<BillingPayment> {
    const res = await apiFetch(`/api/billing/payments/${encodeURIComponent(paymentId)}/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
    });
    if (!res.ok) await unwrapError(res, "refreshBillingPayment failed");
    const data = await res.json();
    if (!data.success) throw new BillingApiError(data.error || "Failed to refresh payment");
    return data.payment;
}

/**
 * Hand the browser to the GoPay gateway. Separated so component tests can
 * mock the navigation away.
 */
export function redirectToPaymentGateway(url: string): void {
    window.location.assign(url);
}
